// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import UserSettingsState from '@app/features/user/state/UserSettings';
import Window from '@app/features/window/state/Window';

const logger = new Logger('TabDisguise');

export const TAB_DISGUISE_MODE_NONE = 'none';
export const TAB_DISGUISE_MODE_DRIVE = 'drive';
export const TAB_DISGUISE_MODE_CLASSROOM = 'classroom';
export const TAB_DISGUISE_MODE_CUSTOM = 'custom';

export interface TabDisguisePreset {
	mode: string;
	title: string;
	favicon: string;
}

export const TAB_DISGUISE_PRESETS: Record<string, TabDisguisePreset> = {
	[TAB_DISGUISE_MODE_DRIVE]: {
		mode: TAB_DISGUISE_MODE_DRIVE,
		title: 'Home - Google Drive',
		favicon:
			'https://thumb.wikimedia.org/wikipedia/commons/thumb/5/5f/Google_Drive_icon_%282026%29.svg/960px-Google_Drive_icon_%282026%29.svg.png?utm_source=commons.wikimedia.org&utm_campaign=imageinfo&utm_content=thumbnail',
	},
	[TAB_DISGUISE_MODE_CLASSROOM]: {
		mode: TAB_DISGUISE_MODE_CLASSROOM,
		title: 'Home - Classroom',
		favicon:
			'https://thumb.wikimedia.org/wikipedia/commons/thumb/1/19/Google_Classroom_Logo.svg/960px-Google_Classroom_Logo.svg.png?utm_source=commons.wikimedia.org&utm_campaign=imageinfo&utm_content=thumbnail',
	},
};

export function buildGoogleFaviconUrl(domain: string): string {
	return `https://www.google.com/s2/favicons?sz=64&domain_url=${encodeURIComponent(domain.trim())}`;
}

export interface ResolvedTabDisguise {
	title: string;
	favicon: string;
}

export function resolveTabDisguise(): ResolvedTabDisguise | null {
	const mode = UserSettingsState.tabDisguiseMode;
	if (mode === TAB_DISGUISE_MODE_DRIVE || mode === TAB_DISGUISE_MODE_CLASSROOM) {
		const preset = TAB_DISGUISE_PRESETS[mode];
		if (!preset) {
			return null;
		}
		return {title: preset.title, favicon: preset.favicon};
	}
	if (mode === TAB_DISGUISE_MODE_CUSTOM) {
		const title = (UserSettingsState.tabDisguiseTitle ?? '').trim();
		const favicon = (UserSettingsState.tabDisguiseFavicon ?? '').trim();
		if (!title || !favicon) {
			return null;
		}
		return {title, favicon};
	}
	return null;
}

export function shouldDisguiseTab(): boolean {
	if (typeof document === 'undefined') {
		return false;
	}
	if (!resolveTabDisguise()) {
		return false;
	}
	return !Window.focused || !Window.visible;
}

const stashedIconHrefs = new Map<HTMLLinkElement, string>();

function getIconLinks(): Array<HTMLLinkElement> {
	if (typeof document === 'undefined') {
		return [];
	}
	return Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel="icon"]'));
}

export function applyDisguiseFavicon(favicon: string): void {
	if (typeof document === 'undefined') {
		return;
	}
	try {
		for (const link of getIconLinks()) {
			if (!stashedIconHrefs.has(link)) {
				stashedIconHrefs.set(link, link.href);
			}
			if (link.href !== favicon) {
				link.href = favicon;
			}
		}
	} catch (error) {
		logger.warn('Failed to apply disguise favicon', error);
	}
}

export function restoreDisguiseFavicon(): void {
	if (typeof document === 'undefined') {
		stashedIconHrefs.clear();
		return;
	}
	try {
		for (const [link, href] of stashedIconHrefs) {
			if (link.isConnected && link.href !== href) {
				link.href = href;
			}
		}
	} catch (error) {
		logger.warn('Failed to restore favicon', error);
	}
	stashedIconHrefs.clear();
}
